import { useEffect, useRef, useState } from 'react';
import type { ShiftStaffingDemandCell, ShiftStaffingDemandPatch, ShiftStaffingDemandSetting, ShiftStaffingDemandWeek, ShiftWeekdayPriority } from 'shared-models';
import type { ShiftStaffingPlan, ShiftScheduleDraftSnapshot } from 'shared-models';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { shiftScheduleService } from '../../services/schedule/shift-schedule.service';
import { getCurrentWeekStartIsoDate, resolveWeekStartFromIsoDate } from '../../utils/date.utils';

type Setting = ShiftStaffingDemandSetting & { weekStart: string | null };
type Draft = { roleId: number; weekday: number; value: string; expectedRevision: number };
type PriorityDraft = { shiftId: number; weekday: number; value: string; expectedRevision: number };

function applyPriority(snapshot: ShiftStaffingDemandWeek, setting: ShiftWeekdayPriority): ShiftStaffingDemandWeek {
  return { ...snapshot, priorities: snapshot.priorities?.map(cell =>
    cell.shiftId === setting.shiftId && cell.weekday === setting.weekday && setting.revision > cell.revision
      ? { ...cell, priority: setting.priority, revision: setting.revision } : cell) };
}

function applySetting(snapshot: ShiftStaffingDemandWeek, setting: Setting): ShiftStaffingDemandWeek {
  if (setting.weekStart !== null && setting.weekStart !== snapshot.weekStart) return snapshot;
  const isDefault = setting.weekStart === null;
  return { ...snapshot, roles: snapshot.roles.map(role => role.id !== setting.roleId ? role : {
    ...role, cells: role.cells.map(cell => {
      if (cell.weekday !== setting.weekday || setting.revision <= (isDefault ? cell.defaultRevision : cell.overrideRevision)) return cell;
      const changed = { ...cell, ...(isDefault
        ? { defaultCount: setting.requiredCount, defaultRevision: setting.revision }
        : { overrideCount: setting.requiredCount, overrideRevision: setting.revision }) };
      const requiredCount = changed.overrideCount ?? changed.defaultCount;
      return { ...changed, requiredCount, shortfall: requiredCount === null ? null : Math.max(0, requiredCount - changed.assignedCount) };
    }),
  }) };
}

function applyDraft(snapshot: ShiftStaffingDemandWeek, draft: ShiftScheduleDraftSnapshot): ShiftStaffingDemandWeek {
  if (snapshot.weekStart !== draft.weekStartDate || draft.revision < (snapshot.staffingRevision ?? 0)) return snapshot;
  let updated: ShiftStaffingDemandWeek = { ...snapshot, staffingRevision: draft.revision, roles: snapshot.roles.flatMap(role => {
    const canonical = draft.staffing.roles.find(current => current.id === role.id);
    return canonical ? [canonical] : [];
  }), priorities: draft.staffing.priorities?.filter(cell => snapshot.priorities?.some(current => current.shiftId === cell.shiftId)) };
  for (const role of snapshot.roles) for (const cell of role.cells) {
    updated = applySetting(updated, { roleId: role.id, weekday: cell.weekday, weekStart: null,
      requiredCount: cell.defaultCount, revision: cell.defaultRevision });
    updated = applySetting(updated, { roleId: role.id, weekday: cell.weekday, weekStart: snapshot.weekStart,
      requiredCount: cell.overrideCount, revision: cell.overrideRevision });
  }
  return updated;
}

export function useStaffingDemandPageController() {
  const [weekStart, setWeekStart] = useState(getCurrentWeekStartIsoDate);
  const [offset, setOffset] = useState(0);
  const [scope, setScope] = useState<ShiftStaffingDemandPatch['scope']>('week');
  const [data, setData] = useState<ShiftStaffingDemandWeek | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [priorityDrafts, setPriorityDrafts] = useState<Record<string, PriorityDraft>>({});
  const [plan, setPlan] = useState<ShiftStaffingPlan | null>(null);
  const [planOutdated, setPlanOutdated] = useState(false);
  const [acknowledgeUnfilled, setAcknowledgeUnfilled] = useState(false);
  const [planOffset, setPlanOffset] = useState(0);
  const [unfilledOffset, setUnfilledOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isOutdated, setIsOutdated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const saveLock = useRef(false);
  const overlays = useRef(new Map<string, Setting>());
  const priorityOverlays = useRef(new Map<string, ShiftWeekdayPriority>());
  const draftOverlays = useRef(new Map<string, ShiftScheduleDraftSnapshot>());
  const eventsReady = useShiftEvents(event => {
    if (event.type === 'schedule.draftChanged') {
      const draft = event.snapshot;
      if ((draftOverlays.current.get(draft.weekStartDate)?.revision ?? 0) >= draft.revision) return;
      draftOverlays.current.set(draft.weekStartDate, draft);
      if (draft.weekStartDate === weekStart) setPlanOutdated(true);
      setData(current => {
        if (!current) return current;
        let updated = applyDraft(current, draft);
        for (const setting of priorityOverlays.current.values()) updated = applyPriority(updated, setting);
        return updated;
      });
      return;
    }
    if (event.type === 'sse.resync' || event.type === 'staffingDemand.upserted' || event.type === 'weekdayPriority.upserted'
      || event.type === 'employeePriority.upserted' || event.type === 'employeeEligibility.upserted'
      || event.type === 'employeeLimits.upserted' || event.type === 'governance.changed'
      || event.type === 'vacation.upserted' || event.type === 'vacation.removed') setPlanOutdated(true);
    if (event.type === 'sse.resync') setReload(current => current + 1);
    else if (event.type === 'weekdayPriority.upserted') {
      const setting = event.setting;
      const key = `${setting.shiftId}:${setting.weekday}`;
      if ((priorityOverlays.current.get(key)?.revision ?? -1) >= setting.revision) return;
      priorityOverlays.current.set(key, setting);
      setData(current => current ? applyPriority(current, setting) : current);
    }
    else if (event.type === 'home.assignmentsChanged' || event.type === 'staffingDemand.invalidated') setIsOutdated(true);
    else if (event.type === 'staffingDemand.upserted') {
      const setting = event.setting;
      if (setting.weekStart !== null && setting.weekStart !== weekStart) return;
      const key = `${setting.weekStart}:${setting.roleId}:${setting.weekday}`;
      if ((overlays.current.get(key)?.revision ?? 0) >= setting.revision) return;
      overlays.current.set(key, setting);
      setData(current => current ? applySetting(current, setting) : current);
    }
  });

  useEffect(() => {
    if (!eventsReady) return;
    let active = true;
    overlays.current.clear();
    setIsLoading(true);
    setData(current => current?.weekStart === weekStart && current.pagination.offset === offset ? current : null);
    setIsOutdated(false);
    setError(null);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        let snapshot = await shiftScheduleService.getStaffingDemand(weekStart, offset);
        if (!active) return;
        const draft = draftOverlays.current.get(weekStart);
        if (draft) snapshot = applyDraft(snapshot, draft);
        for (const setting of overlays.current.values()) snapshot = applySetting(snapshot, setting);
        for (const setting of snapshot.priorities ?? []) {
          const key = `${setting.shiftId}:${setting.weekday}`;
          if ((priorityOverlays.current.get(key)?.revision ?? -1) < setting.revision) priorityOverlays.current.set(key, setting);
        }
        for (const setting of priorityOverlays.current.values()) snapshot = applyPriority(snapshot, setting);
        setData(snapshot);
      } catch {
        if (active) setError('לא ניתן לטעון את מכסות כוח האדם.');
      } finally {
        if (active) setIsLoading(false);
      }
    });
    return () => { active = false; };
  }, [weekStart, offset, eventsReady, reload]);

  const pending = Object.values(drafts);
  const pendingPriorities = Object.values(priorityDrafts);
  const hasDemandChanges = pending.length > 0;
  const hasPriorityChanges = pendingPriorities.length > 0;
  const hasChanges = hasDemandChanges || hasPriorityChanges;
  const isPriorityValid = pendingPriorities.every(draft => draft.value !== '' && Number.isInteger(Number(draft.value))
    && Number(draft.value) >= 0 && Number(draft.value) <= 100);
  const isValid = isPriorityValid && pending.every(draft => draft.value === '' || (Number.isInteger(Number(draft.value)) && Number(draft.value) >= 0 && Number(draft.value) <= 1000));
  const hasConflicts = pendingPriorities.some(draft => {
    const cell = data?.priorities?.find(current => current.shiftId === draft.shiftId && current.weekday === draft.weekday);
    return !cell || cell.revision !== draft.expectedRevision;
  }) || pending.some(draft => {
    const cell = data?.roles.find(role => role.id === draft.roleId)?.cells.find(current => current.weekday === draft.weekday);
    return !cell || draft.expectedRevision !== (scope === 'default' ? cell.defaultRevision : cell.overrideRevision);
  });
  const groupedPriorities = new Map<number, NonNullable<ShiftStaffingDemandWeek['priorities']>>();
  for (const setting of data?.priorities ?? []) {
    const cells = groupedPriorities.get(setting.shiftId) ?? [];
    cells.push(setting);
    groupedPriorities.set(setting.shiftId, cells);
  }

  const onPreviewPlan = async () => {
    if (saveLock.current || isLoading || hasChanges || !data?.canPlan) return;
    saveLock.current = true;
    setIsSaving(true);
    setError(null);
    setPlan(null);
    setPlanOutdated(false);
    setAcknowledgeUnfilled(false);
    setPlanOffset(0);
    setUnfilledOffset(0);
    try {
      setPlan(await shiftScheduleService.getStaffingPlan(weekStart));
    } catch {
      setError('לא ניתן להכין שיבוץ אוטומטי. התכנון מוגבל ל-100 תפקידים, 200 עובדים ו-300 משבצות בשבוע.');
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };

  const onApplyPlan = async () => {
    if (saveLock.current || isLoading || hasChanges || !data?.canPlan || !plan || plan.weekStart !== weekStart
      || planOutdated || !plan.assignments.length || (plan.unfilled.length > 0 && !acknowledgeUnfilled)) return;
    saveLock.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await shiftScheduleService.applyStaffingPlan({ weekStart: plan.weekStart, expectedFingerprint: plan.fingerprint, acknowledgeUnfilled });
      setPlan(null);
    } catch {
      setPlanOutdated(true);
      setError('השיבוץ לא הוחל. יש להכין תצוגה חדשה; ייתכן שהנתונים השתנו.');
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };

  const onChangePriority = (cell: ShiftWeekdayPriority, value: string) => {
    if (!data?.canEdit || isLoading || saveLock.current) return;
    const key = `${cell.shiftId}:${cell.weekday}`;
    setPlanOutdated(true);
    setPriorityDrafts(current => {
      const next = { ...current };
      if (value !== '' && Number(value) === cell.priority) delete next[key];
      else next[key] = { shiftId: cell.shiftId, weekday: cell.weekday, value,
        expectedRevision: current[key]?.expectedRevision ?? cell.revision };
      return next;
    });
  };

  const onSavePriorities = async () => {
    if (saveLock.current || isLoading || !data?.canEdit || !hasPriorityChanges || !isValid || hasConflicts) return;
    saveLock.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await shiftScheduleService.patchWeekdayPriorities({ changes: pendingPriorities.map(draft => ({
        shiftId: draft.shiftId, weekday: draft.weekday, priority: Number(draft.value), expectedRevision: draft.expectedRevision,
      })) });
      setPriorityDrafts({});
    } catch {
      setError('לא ניתן לשמור את העדיפויות. ייתכן שהנתונים השתנו; העריכות שלך נשמרו במסך.');
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };

  const onChangeCell = (roleId: number, cell: ShiftStaffingDemandCell, value: string) => {
    if (!data?.canEdit || isLoading || saveLock.current) return;
    const key = `${roleId}:${cell.weekday}`;
    setPlanOutdated(true);
    const baseline = scope === 'default' ? cell.defaultCount : cell.overrideCount;
    const revision = scope === 'default' ? cell.defaultRevision : cell.overrideRevision;
    setDrafts(current => {
      const next = { ...current };
      if ((value === '' ? null : Number(value)) === baseline) delete next[key];
      else next[key] = { roleId, weekday: cell.weekday, value, expectedRevision: current[key]?.expectedRevision ?? revision };
      return next;
    });
  };

  const onSaveDemand = async () => {
    if (saveLock.current || isLoading || !data?.canEdit || !hasDemandChanges || !isValid || hasConflicts) return;
    saveLock.current = true;
    setIsSaving(true);
    setError(null);
    try {
      await shiftScheduleService.patchStaffingDemand({ scope, weekStart, changes: pending.map(draft => ({
        roleId: draft.roleId, weekday: draft.weekday, requiredCount: draft.value === '' ? null : Number(draft.value),
        expectedRevision: draft.expectedRevision,
      })) });
      setDrafts({});
    } catch {
      setError('לא ניתן לשמור. ייתכן שהנתונים השתנו; העריכות שלך נשמרו במסך.');
    } finally {
      saveLock.current = false;
      setIsSaving(false);
    }
  };

  return {
    data, weekStart, offset, scope, drafts, hasChanges, hasConflicts, isValid, isLoading, isSaving, isOutdated, error,
    priorityDrafts, priorityRows: [...groupedPriorities.values()], hasDemandChanges, hasPriorityChanges, isPriorityValid,
    onChangePriority, onSavePriorities,
    plan: plan?.weekStart === weekStart ? plan : null, planOutdated, acknowledgeUnfilled, setAcknowledgeUnfilled,
    planOffset, setPlanOffset, unfilledOffset, setUnfilledOffset, onPreviewPlan, onApplyPlan,
    onChangeCell, onSaveDemand,
    setScope,
    setOffset,
    onChangeWeek: (value: string) => { if (value) { setWeekStart(resolveWeekStartFromIsoDate(value)); setOffset(0); } },
    onDiscardChanges: () => { setDrafts({}); setPriorityDrafts({}); setError(null); },
    onRefresh: () => setReload(current => current + 1),
  };
}